import { RiskAssessmentRecord } from '@models/risk-assessment';

import type { GetAssessmentQuery } from 'generated/assessment.generated';
import type { SharedAssessmentResultSharingFieldsFragment } from 'generated/fragments.generated';
import type { RiskAssessmentModel } from 'generated/types';

/**
 * The demographics a DHA result needs to pick its reference ranges.
 *
 * A deliberate subset: the authenticated view reads these off `getScanProfile`,
 * which also carries the patient's name, and the shared view must never receive
 * more than the result requires.
 */
export type AssessmentProfile = {
  age?: number | null;
  sex?: string | null;
  smoker_status?: number | null;
  height?: number | null;
  height_denomination?: string | null;
  weight?: number | null;
  weight_denomination?: string | null;
};

/**
 * The single shape every DHA result view renders from, whichever source it came
 * from. Keeping this explicit is what lets the external share page reuse the
 * authenticated result tree without synthesising a fake query response.
 */
export type AssessmentResultData = {
  riskAssessment: RiskAssessmentRecord | null;
  profile: AssessmentProfile | null;
};

/**
 * `smoker_status` is an Int on both sources, but the authenticated query types
 * it loosely enough that a string can arrive. Normalise rather than trusting it.
 */
function toNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/** Authenticated source: the `getAssessment` + `getScanProfile` query pair. */
export function fromGetAssessmentQuery(
  data?: GetAssessmentQuery | null
): AssessmentResultData {
  if (!data) {
    return { riskAssessment: null, profile: null };
  }
  const riskAssessment = data.getAssessment.risk_assessment
    ? new RiskAssessmentRecord(
        data.getAssessment.risk_assessment as RiskAssessmentModel
      )
    : null;

  // Non-nullable in the schema: getAssessment and getScanProfile are queried
  // together, so a result always arrives with its demographics.
  const scanProfile = data.getScanProfile;
  return {
    riskAssessment,
    profile: {
      age: toNumber(scanProfile.age),
      sex: scanProfile.sex,
      smoker_status: toNumber(scanProfile.smoker_status),
      height: toNumber(scanProfile.height),
      height_denomination: scanProfile.height_denomination,
      weight: toNumber(scanProfile.weight),
      weight_denomination: scanProfile.weight_denomination,
    },
  };
}

/** External source: one `SharedAssessmentResultModel` off a share link. */
export function fromSharedAssessmentResult(
  record?: SharedAssessmentResultSharingFieldsFragment | null
): AssessmentResultData {
  if (!record) {
    return { riskAssessment: null, profile: null };
  }
  // The share payload's risk assessment is a SharedRiskAssessmentModel: the
  // same fields RiskAssessmentRecord reads (occurrenceDateTime, method,
  // prediction, observations), minus everything an anonymous recipient must not
  // receive -- notably `subject`, which is why this is a cast rather than an
  // assignment. Narrower on purpose; see the backend's sanitizeSharedRiskAssessment.
  const riskAssessment = record.risk_assessment
    ? new RiskAssessmentRecord(
        record.risk_assessment as unknown as RiskAssessmentModel
      )
    : null;

  return {
    riskAssessment,
    profile: record.profile
      ? {
          age: toNumber(record.profile.age),
          sex: record.profile.sex,
          smoker_status: toNumber(record.profile.smoker_status),
          height: toNumber(record.profile.height),
          height_denomination: record.profile.height_denomination,
          weight: toNumber(record.profile.weight),
          weight_denomination: record.profile.weight_denomination,
        }
      : null,
  };
}
